/**
 * Solana money hook — the frontend half of the deposit-to-PDA model. Reads the
 * user's withdrawable balance (UserAccount.available) and lets them deposit /
 * withdraw SOL, signed by their Privy Solana embedded wallet. The heavy escrow
 * module (anchor + web3.js) is dynamically imported so it stays out of the init
 * bundle — it only loads when the wallet panel actually mounts.
 *
 * Privy is used to SIGN only (useSignTransaction); broadcasting + confirming is
 * done in-app by escrow.sendSignedTransaction. Privy's signAndSendTransaction
 * used to throw a wallet-connection error AFTER the tx had landed (its confirm
 * needs a signatureSubscribe websocket the prod RPC doesn't offer), so a
 * withdraw looked failed, the user retried, and it paid out twice.
 *
 * Active only when VITE_NETWORK=solana (see config.SOLANA_NETWORK); the EVM money
 * hooks (useGames / useVault) stay the default otherwise.
 */
import { useCallback, useEffect, useState } from "react";
import { useWallets, useSignTransaction } from "@privy-io/react-auth/solana";
import { solToLamports } from "../contracts/solana/config";
import { subscribeVaultDirty } from "../auth/quickPlayStore";

type SerializableTx = {
  serialize(opts: { requireAllSignatures: boolean; verifySignatures: boolean }): Uint8Array;
  /** Set by escrow.withFeePayer — when the tx's blockhash dies (see sendSignedTransaction). */
  lastValidBlockHeight?: number;
};

export function useSolanaMoney(walletAddress: string | null) {
  const { wallets } = useWallets();
  const { signTransaction } = useSignTransaction();
  const wallet = wallets.find((w) => w.address === walletAddress) ?? wallets[0];

  const [available, setAvailable] = useState<bigint | null>(null);
  // Un-deposited native SOL still sitting in the Privy embedded wallet. Read
  // alongside `available` so the headline balance never shows $0 just because
  // the funds haven't been moved into the Vault yet — mirrors the EVM header,
  // which sums embedded-wallet USDT + in-vault USDT.
  const [walletBalance, setWalletBalance] = useState<bigint | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!walletAddress) {
      setAvailable(null);
      setWalletBalance(null);
      return;
    }
    const { getAvailable, getWalletBalance } = await import("../contracts/solana/escrow");
    const [av, wb] = await Promise.all([
      getAvailable(walletAddress).catch(() => 0n),
      getWalletBalance(walletAddress).catch(() => 0n),
    ]);
    setAvailable(av);
    setWalletBalance(wb);
  }, [walletAddress]);

  // Poll the on-chain balance while the panel is mounted.
  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 15_000);
    return () => clearInterval(t);
  }, [refresh]);

  // Re-read immediately when a quick-play sweep lands (backend just credited
  // the Vault) — the enable flow flips to "ready" without a poll-tick wait.
  useEffect(() => subscribeVaultDirty(() => void refresh()), [refresh]);

  const send = useCallback(
    async (build: (w: string, l: bigint) => Promise<SerializableTx>, sol: number): Promise<string> => {
      if (!walletAddress || !wallet) throw new Error("no Solana wallet connected");
      setBusy(true);
      try {
        const tx = await build(walletAddress, solToLamports(sol));
        const bytes = tx.serialize({ requireAllSignatures: false, verifySignatures: false });
        // Sign WITHOUT sending — if this throws, nothing reached the network,
        // so surfacing the error is safe and a retry can't double-send.
        const { signedTransaction } = await signTransaction({ transaction: new Uint8Array(bytes), wallet });
        // Broadcast + confirm in-app: idempotent re-sends of the same signed
        // bytes, error only once the tx definitively cannot land.
        const { sendSignedTransaction } = await import("../contracts/solana/escrow");
        const signature = await sendSignedTransaction(signedTransaction, tx.lastValidBlockHeight);
        // Refresh in the background: the withdraw card is sitting on this
        // await to show its confirmation, and two more balance reads would
        // hold it back — the hook's state updates when they land (~1s).
        void refresh();
        // base58 tx signature — callers just need a truthy id.
        return signature;
      } finally {
        setBusy(false);
      }
    },
    [walletAddress, wallet, signTransaction, refresh],
  );

  const deposit = useCallback(
    async (sol: number) => {
      const { buildDepositTx } = await import("../contracts/solana/escrow");
      return send(buildDepositTx, sol);
    },
    [send],
  );

  // Withdraw pays out to an EXTERNAL wallet from the FULL balance (Vault
  // `available` + un-swept wallet SOL, so quick play is never required):
  // program withdraw + System transfer composed into one atomic tx (see
  // buildWithdrawToTx) so the auto-sweep never sees the funds in the wallet.
  const withdraw = useCallback(
    async (sol: number, destination: string) => {
      const { buildWithdrawToTx } = await import("../contracts/solana/escrow");
      return send((w, l) => buildWithdrawToTx(w, l, destination), sol);
    },
    [send],
  );

  return { available, walletBalance, busy, deposit, withdraw, refresh };
}
